/**
 * planningModeStyles — token-only inline style objects for the Planning list
 * (T12.1.5.1.2). Mirrors the prototype's `#s-list-plan` look. No hex, no raw
 * pixel literals (CP5). Touch-first: interactive targets clear 2.75rem (44+).
 *
 * The prototype reference comments below cite the prototype's hand-tuned sizes
 * as bare numbers (pixels, e.g. "13 / 14" = 13/14 padding); T12.1.5.1.7 added
 * the matching fine token tiers so these resolve without rounding up.
 *
 * The same components render at both widths; desktop gets a centered max-width
 * column from the page wrapper, not different rows (parent § Responsive scope).
 */

import type { CSSProperties } from 'react';

export const cardStyle: CSSProperties = {
  backgroundColor: 'var(--color-bg-raised)',
  borderWidth: '0.0625rem',
  borderStyle: 'solid',
  borderColor: 'var(--color-border-subtle)',
  borderRadius: 'var(--radius-lg)',
  boxShadow: 'var(--shadow-sm)',
  marginBottom: 'var(--space-3)',
  overflow: 'hidden',
  // The remove (✕) is absolutely pinned to this card's top-right corner
  // (`removeStyle`), so the card is the positioning context. Anchoring the ✕ to
  // the card top + the row's own top padding token (rather than a fixed pixel
  // offset over a 44px-centered glyph) lets it track the name line as the
  // device's font/display scale grows — Samsung One UI's larger default scale
  // moved the name line but left the old fixed-offset ✕ behind, misaligning it.
  position: 'relative',
};

// Wraps the tap-to-expand row button. The remove (✕) is a sibling but is
// absolutely pinned to the card corner (`removeStyle`) rather than placed in
// flow — it can't nest inside the row button (nested interactive), and pinning
// it (not flowing it) is what lets it track the name line under font scaling.
export const rowHeaderStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
};

export const rowStyle: CSSProperties = {
  display: 'flex',
  // Top-align the row content so the NAME line's top is ALWAYS the row's top
  // padding (`--space-3h`) below the card top — for a one-line item AND a
  // two-line item alike. The pinned ✕ (`removeStyle`) is anchored at that same
  // `top: --space-3h`, so the glyph lands on the name line in both cases. With
  // the prior `alignItems: center`, a one-line name floated to the row's
  // vertical middle (the row is taller than one text line because of the thumb),
  // leaving the top-pinned ✕ above it; top-aligning removes that one/two-line
  // divergence. The thumb top-aligns with the name — reads clean on the compact
  // list row.
  alignItems: 'flex-start',
  gap: 'var(--space-3)',
  // Prototype `.row`: 13 / 14 padding (the 4-step scale rounded this to 12/16).
  // The TRAILING padding reserves room for the absolutely-pinned ✕
  // (`removeStyle`, hung off the card's top-right corner). The row content (and
  // the trailing offer summary) stop short of the ✕ so they never underlap it
  // ("best✕"). The reserve is the ✕ GLYPH width (≈ one `--font-size-base` em),
  // plus its trailing inset from the card edge (`--space-3q`), plus a small gap
  // (`--space-2`) so the `$X best` pill keeps clear of the glyph at 360px. All
  // three terms are tokens, so the reserve grows with the font scale exactly as
  // the glyph does — no fixed pixel reservation that goes stale under a larger
  // device scale.
  padding:
    'var(--space-3h) calc(var(--font-size-base) + var(--space-3q) + var(--space-2)) var(--space-3h) var(--space-3q)',
  minHeight: '2.75rem',
  cursor: 'pointer',
  textAlign: 'left',
  width: '100%',
  // The row button sits beside the trailing remove (✕) inside `rowHeaderStyle`
  // (a flex row); the card clips overflow. A flex item defaults to
  // `min-width: auto`, so this `width:100%` button refused to shrink below its
  // min-content (thumb + the name floor + the offer summary). `minWidth: 0` lets
  // the button yield space so the ✕ always shows; inside it the status column
  // (`statusColStyle`) shrinks — the short `summaryLabel` stays one line and the
  // no-offer state is a tiny dot — before the protected name floor (`metaStyle`)
  // gives.
  minWidth: 0,
  background: 'transparent',
  border: 'none',
  color: 'inherit',
  fontFamily: 'var(--font-family-sans)',
};

export const thumbStyle: CSSProperties = {
  // Prototype `#s-list-plan .card .thumbimg`: 38px (compact). The shopping list
  // keeps its larger thumb; this is the planning-row size only.
  width: '2.375rem',
  height: '2.375rem',
  borderRadius: 'var(--radius-md)',
  backgroundColor: 'var(--color-bg-muted)',
  display: 'grid',
  placeItems: 'center',
  fontSize: 'var(--font-size-lg)',
  flex: '0 0 auto',
};

export const metaStyle: CSSProperties = {
  flex: 1,
  // F4 (T27.2.5.5) — protect the item-name column. At 360px the trailing status
  // chip ("Not at your stores") + caret previously won the row's width and the
  // name collapsed to a single letter ("A…", "G…") — the most important thing on
  // the row became unreadable. A non-trivial `minWidth` floor keeps the name
  // legible; the trailing status column (`statusColStyle`) carries `minWidth: 0`
  // so its chip wraps / shrinks first. The floor stays below the narrowest
  // device width minus the thumb + status column so it never forces overflow.
  // The 9rem floor holds the full short name line ("Cucumber · 1 bottle") at 360px;
  // the long status chip wraps in its narrowed column rather than starving it.
  minWidth: '9rem',
};

export const nameStyle: CSSProperties = {
  fontWeight: 'var(--font-weight-semibold)',
  // Prototype `.nm`: 15 (base rounded this up to 16).
  fontSize: 'var(--font-size-base15)',
  lineHeight: 'var(--line-height-tight)',
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-1)',
  // The header stays one stable line whether or not a brand second line
  // follows — the prototype's `.nm` never wraps. The title truncates
  // (titleTextStyle) and the qty flag stays pinned to the trailing edge so the row
  // reads the same name+qty / brand two-row rhythm with or without brand data.
  flexWrap: 'nowrap',
  minWidth: 0,
};

// The item-name text itself — truncates with an ellipsis so a long no-brand
// name (e.g. "Chicken Breast") can't push the qty flag + ⚙ onto their own line.
export const titleTextStyle: CSSProperties = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  minWidth: 0,
};

export const qtyFlagStyle: CSSProperties = {
  color: 'var(--color-text-muted)',
  fontWeight: 'var(--font-weight-regular)',
  // Prototype `.qtyflag`: 13.
  fontSize: 'var(--font-size-xs)',
  // Pinned to the trailing edge — never wraps below the (truncating) title.
  flex: '0 0 auto',
  whiteSpace: 'nowrap',
};

export const brandLineStyle: CSSProperties = {
  // Prototype `.brandline`: 12.5 (sm rounded this up to 14), a normal-height
  // text line directly under the name. Display-only — the collapsed row is
  // tap-to-expand only, so the brand line carries no settings deep-link; a tap
  // on it bubbles to the row and opens it. The interactive button affordances
  // (cursor, button reset) are gone — this reads as plain text.
  fontSize: 'var(--font-size-2sm)',
  color: 'var(--color-text-muted)',
  marginTop: 'var(--space-1)',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 'var(--space-1)',
};

export const starStyle: CSSProperties = {
  color: 'var(--color-status-warning)',
};

// `notedot` — a small dot on the collapsed row when a note exists (deliverable 3,
// matching the shopping `notedot`). Brand-tinted, wordless; sits on the name line
// trailing edge (a note can exist without a brand/size line, so it can't live on
// the brandLine). Always-present line, so the dot is reliably visible.
export const noteDotStyle: CSSProperties = {
  display: 'inline-block',
  width: '0.3125rem',
  height: '0.3125rem',
  borderRadius: 'var(--radius-pill)',
  backgroundColor: 'var(--color-accent-primary)',
  flex: '0 0 auto',
};

// T12.1.8.7 — "View deal" affordance on a flyer-added item. A quiet pill that
// reopens the exact added deal (`/deals?deal=<flyerdeal_id>`). Sits on the
// brand/meta line; tinted like the sale chip so it reads as deal-related without
// competing with the status chip. Opts out of the global
// `[role='button']{min-height:2.75rem}` rule (it's an inline pill, not a
// full-height target — the parent row stays the large tap target).
export const dealLinkStyle: CSSProperties = {
  minHeight: 0,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 'var(--space-1)',
  marginTop: 'var(--space-1)',
  // Prototype deal pill scale: ~12.5.
  fontSize: 'var(--font-size-2sm)',
  fontWeight: 'var(--font-weight-semibold)',
  color: 'var(--color-status-danger)',
  backgroundColor: 'var(--color-status-danger-subtle, var(--color-bg-muted))',
  padding: 'var(--space-1) var(--space-2)',
  borderRadius: 'var(--radius-pill)',
  border: 'none',
  cursor: 'pointer',
  fontFamily: 'var(--font-family-sans)',
  alignSelf: 'flex-start',
};

export const statusColStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  gap: 'var(--space-1)',
  // F4 (T27.2.5.5) — the trailing offer summary gives way BEFORE the name. Was
  // `flex: 0 0 auto` (never shrinks, so it took whatever width its content
  // needed and starved the name at 360px). Now it may shrink (`0 1 auto`) down
  // to a capped width. The summary that lives here is at most a short one-line
  // `summaryLabel` (`whiteSpace: nowrap`) or the tiny wordless `availabilityDot`
  // — never a long wrapping phrase — so the cap leaves the name its full floor
  // (`metaStyle`) at 360px without anything being cut off.
  flex: '0 1 auto',
  minWidth: 0,
  maxWidth: '6.5rem',
  // Top-align the summary column to the NAME line. The row button is
  // `alignItems: center`, so on a two-line item (name line + brandLine) the
  // centered summary floated to the row's vertical middle — stranded between the
  // two text lines. `alignSelf: 'flex-start'` rises it to the row button's top,
  // which (same top padding context as `metaStyle`) lines the pill up with the
  // name line per the contract § Row anatomy collapsed diagram. On a single-line
  // item this is a no-op (top === middle of one line). The thumb + meta stay
  // centered. The summary no longer references the ✕'s position at all — the row
  // reserves trailing room for the absolutely-pinned ✕, so the summary simply
  // right-anchors to the row content edge and the gap to the ✕ comes from that
  // reserved padding, not a margin tuned to the ✕ glyph.
  alignSelf: 'flex-start',
};

// Row-level remove (✕) — the prototype's per-card remove. Quiet by default so it
// doesn't compete with the chip; 44+ target. Sits at the row's trailing edge.
//
// Tone: `--color-border-strong`, the neutral quiet-grey mark token (the same one
// the wordless `availabilityDot` + the starter ＋ glyph use here), NOT
// `--color-text-muted`. In dark, `text-muted` is a blue-grey (`darkText500`)
// that read too saturated for a passive remove control; the border-strong grey
// is a quieter, hue-neutral grey in both themes.
export const removeStyle: CSSProperties = {
  // Pin the ✕ to the card's top-right corner (`cardStyle` is the positioning
  // context). `top` is the row's OWN top padding token (`--space-3h`) — the
  // exact distance from the card top to the name line — and `right` is the row's
  // side padding token (`--space-3q`). Because the name line and the ✕ are both
  // measured from the card top by the SAME token, they track together as the
  // device's font/display scale grows: the prior fix used a fixed pixel offset
  // (`paddingTop` over a 44px-grid-centered glyph) that assumed one font metric,
  // so Samsung One UI's larger default scale moved the name line but left the ✕
  // behind. Absolute positioning removes that coupling.
  position: 'absolute',
  top: 'var(--space-3h)',
  // Pin the box's RIGHT edge to the card's right edge; the glyph is right-aligned
  // inside the box with a trailing inset (`paddingRight: --space-3q`) so the ✕
  // sits a consistent, token-scaled distance from the card edge — not floated
  // ~38px in (which the centered-glyph box did). The 44px box width keeps a wide
  // tap target reaching left over the row's reserved padding.
  right: '0',
  paddingRight: 'var(--space-3q)',
  // Keep the 2.75rem tap target, but TOP-align the glyph (not grid-centered in
  // the box) so the ✕ sits ON the name line rather than the box's vertical
  // middle. `justifyContent: flex-end` right-aligns it (with the inset above);
  // `alignItems: flex-start` drops it to the top, level with the name text whose
  // top is the same `--space-3h` below the card top. The line-height matches the
  // name line (`--line-height-tight`) so a single glyph's box lines up with it.
  minWidth: '2.75rem',
  minHeight: '2.75rem',
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'flex-start',
  lineHeight: 'var(--line-height-tight)',
  boxSizing: 'border-box',
  fontSize: 'var(--font-size-base)',
  color: 'var(--color-border-strong)',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  fontFamily: 'var(--font-family-sans)',
};

// The collapsed `summaryLabel` — a single short price/sale line in the trailing
// status column. Quiet, low-saturation so a glance down the list reads as a
// list, not ads. Both label cases ("On sale · N stores", "$X best") stay one
// line (`whiteSpace: nowrap`); the column caps + shrinks (statusColStyle) so the
// protected name floor (metaStyle) holds at 360px. The no-offer state is the
// wordless availabilityDot, never a long clippable phrase — so the summary text
// can never be cut off mid-word.
export const summaryLabelStyle: CSSProperties = {
  // Prototype `.chip`: 11.5 (xs rounded this up to 13).
  fontSize: 'var(--font-size-2xs)',
  fontWeight: 'var(--font-weight-semibold)',
  padding: 'var(--space-1) var(--space-2)',
  borderRadius: 'var(--radius-pill)',
  maxWidth: '100%',
  whiteSpace: 'nowrap',
  backgroundColor: 'var(--color-bg-muted)',
  color: 'var(--color-text-secondary)',
};

export const summaryLabelSaleStyle: CSSProperties = {
  ...summaryLabelStyle,
  backgroundColor: 'var(--color-status-danger-subtle, var(--color-bg-muted))',
  color: 'var(--color-status-danger)',
};

export const summaryLabelBestStyle: CSSProperties = {
  ...summaryLabelStyle,
  backgroundColor: 'var(--color-status-success-subtle, var(--color-bg-muted))',
  color: 'var(--color-status-success)',
};

// The `availabilityDot` — the no-offer state of `storeLocationOfferSummary`. A
// small muted dot, NO text, so nothing can be cut off at 360px. It replaces the
// old "Not at your stores" string entirely: the household has resolved stores
// but none carry this item, said quietly with a wordless mark.
export const availabilityDotStyle: CSSProperties = {
  width: '0.5rem',
  height: '0.5rem',
  borderRadius: 'var(--radius-pill)',
  backgroundColor: 'var(--color-border-strong)',
  flex: '0 0 auto',
};

// The cold-start starter row's trailing ＋ add glyph. (The item row has no
// trailing caret — tapping the row opens it — but the starter rows are a
// separate add affordance, not the collapsed item row.)
export const starterAddGlyphStyle: CSSProperties = {
  color: 'var(--color-border-strong)',
  fontSize: 'var(--font-size-xs)',
};

// The opened-up view (`expandedView`) — the whole panel revealed when the row is
// tapped. Renamed from the legacy `availStyle` (which named the block when it was
// only the where-to-buy availability panel; it now holds the full opened-up
// view). The collapsed-row `availabilityDot` is a DIFFERENT name and stays.
export const expandedViewStyle: CSSProperties = {
  borderTopWidth: '0.0625rem',
  borderTopStyle: 'dashed',
  borderTopColor: 'var(--color-border-subtle)',
  padding: 'var(--space-2) var(--space-4) var(--space-3)',
  backgroundColor: 'var(--color-bg-primary)',
};

export const qtyRowStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: 'var(--space-2)',
  padding: 'var(--space-1) 0 var(--space-3)',
  borderBottomWidth: '0.0625rem',
  borderBottomStyle: 'dashed',
  borderBottomColor: 'var(--color-border-subtle)',
  marginBottom: 'var(--space-3)',
};

// Inner horizontal row holding the stepper + the size link, so they always sit
// on one line together below the "How many" label (the label stacks above).
export const stepperRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-3)',
  width: '100%',
};

export const qtyLabelStyle: CSSProperties = {
  // Prototype `.qtyrow .qtylabel`: 13.5.
  fontSize: 'var(--font-size-smPlus)',
  fontWeight: 'var(--font-weight-semibold)',
  color: 'var(--color-text-secondary)',
};

export const stepperStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  borderWidth: '0.0625rem',
  borderStyle: 'solid',
  borderColor: 'var(--color-border-strong)',
  borderRadius: 'var(--radius-md)',
  overflow: 'hidden',
};

export const stepperButtonStyle: CSSProperties = {
  minWidth: '2.75rem',
  minHeight: '2.75rem',
  border: 'none',
  backgroundColor: 'var(--color-bg-muted)',
  color: 'var(--color-text-primary)',
  fontSize: 'var(--font-size-lg)',
  cursor: 'pointer',
};

export const stepperValueStyle: CSSProperties = {
  minWidth: '2.75rem',
  textAlign: 'center',
  fontWeight: 'var(--font-weight-semibold)',
};

// `sizeLink` — the always-shown size control in the expanded `qtyRow`. Mirrors
// the prototype `.qtyrow .unit` (line 905): the leading `· ` separator + trailing
// ` ›` chevron live in the row's text content; this style supplies the muted tone
// + dotted underline (a tappable, plain-text-weight link to product settings).
export const unitStyle: CSSProperties = {
  // Prototype `.qtyrow .unit`: 13.
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-muted)',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  textDecoration: 'underline dotted',
  textUnderlineOffset: '0.125rem',
  fontFamily: 'var(--font-family-sans)',
  marginLeft: 'auto',
};

// The where-to-buy SUMMARY row (`.sd-row` in the prototype): a fixed "Where"
// label + the picked store (else "Select store ▾"). Tapping it reveals the
// existing picker below (`whereToBuyToggle` → `whereToBuyPicker`); the picker's
// own redesign is .9.3. A labelled row, the one labelled row in the panel.
export const whereSummaryRowStyle: CSSProperties = {
  display: 'flex',
  gap: 'var(--space-2h)',
  alignItems: 'baseline',
  // Prototype `.sd-row`: 4px 0.
  padding: 'var(--space-1) 0',
  width: '100%',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  textAlign: 'left',
  color: 'inherit',
  fontFamily: 'var(--font-family-sans)',
  // Inline summary line — not a full-height target (the row + panel are the
  // large affordances). Opts out of the global 44px min-height.
  minHeight: 0,
};

export const whereSummaryLabelStyle: CSSProperties = {
  // Prototype `.sd-row .sd-k`: 10.5, fixed 72px column.
  fontSize: 'var(--font-size-3xs)',
  fontWeight: 'var(--font-weight-bold)',
  textTransform: 'uppercase',
  letterSpacing: '0.03em',
  color: 'var(--color-text-muted)',
  flex: '0 0 4.5rem',
};

export const whereSummaryValueStyle: CSSProperties = {
  flex: 1,
  minWidth: 0,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 'var(--space-1)',
  fontSize: 'var(--font-size-smPlus)',
  color: 'var(--color-accent-primary)',
};

// The ▾ caret on the where summary value (prototype `.wval .cv`).
export const whereSummaryCaretStyle: CSSProperties = {
  color: 'var(--color-text-muted)',
  fontSize: 'var(--font-size-2xs)',
};

// The revealed picker block wrapper (prototype `.wherepick.on`) — gives the
// store list a small top margin when it opens below the summary row.
export const wherePickStyle: CSSProperties = {
  marginTop: 'var(--space-1)',
};

export const whereHeadStyle: CSSProperties = {
  // Prototype `.wherehead`: 11.5.
  fontSize: 'var(--font-size-2xs)',
  fontWeight: 'var(--font-weight-semibold)',
  textTransform: 'uppercase',
  letterSpacing: '0.03em',
  color: 'var(--color-text-muted)',
  margin: 'var(--space-1) 0 var(--space-2)',
};

export const whereOptStyle: CSSProperties = {
  fontWeight: 'var(--font-weight-regular)',
  textTransform: 'none',
  letterSpacing: 0,
};

// .9.6 reopen fix 3 — the store-select button and (one-deal store) the sibling
// "View" trailing it sit in this flex row. The store button keeps its own bottom
// border, so the row reads unchanged when there's no View; the View trails at the
// row's end. A wrapper (not a nested button) keeps the markup valid.
export const storeRowWrapStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
};

export const storeRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-2)',
  padding: 'var(--space-2) 0',
  minHeight: '2.75rem',
  borderBottomWidth: '0.0625rem',
  borderBottomStyle: 'solid',
  borderBottomColor: 'var(--color-border-subtle)',
  cursor: 'pointer',
  width: '100%',
  background: 'transparent',
  border: 'none',
  textAlign: 'left',
  color: 'inherit',
  fontFamily: 'var(--font-family-sans)',
};

export const storeRadioStyle: CSSProperties = {
  width: '1rem',
  height: '1rem',
  borderRadius: 'var(--radius-pill)',
  borderWidth: '0.125rem',
  borderStyle: 'solid',
  borderColor: 'var(--color-border-strong)',
  flex: '0 0 auto',
  position: 'relative',
};

export const storeRadioSelStyle: CSSProperties = {
  ...storeRadioStyle,
  borderColor: 'var(--color-accent-primary)',
  backgroundColor: 'var(--color-accent-primary)',
};

export const storeNameStyle: CSSProperties = {
  flex: 1,
  minWidth: 0,
  // Prototype `.store .sn`: 13.5.
  fontSize: 'var(--font-size-smPlus)',
};

export const storeMatchLineStyle: CSSProperties = {
  // Prototype's inline store sub-line: 11.
  fontSize: 'var(--font-size-2xs)',
  color: 'var(--color-text-secondary)',
  marginTop: 'var(--space-1)',
};

export const qualifierStyle: CSSProperties = {
  color: 'var(--color-accent-primary)',
  fontWeight: 'var(--font-weight-semibold)',
};

// T12.5.1 — "· your pick": the brand of the offer the household selected on its
// list. A positive but weaker signal than a real liked brand, so it reads in the
// accent tone (the household's choice) without the liked-brand emphasis weight.
export const pickQualifierStyle: CSSProperties = {
  color: 'var(--color-accent-primary)',
  fontWeight: 'var(--font-weight-medium, var(--font-weight-semibold))',
};

export const subQualifierStyle: CSSProperties = {
  color: 'var(--color-text-muted)',
  fontWeight: 'var(--font-weight-semibold)',
};

export const priceStyle: CSSProperties = {
  fontWeight: 'var(--font-weight-semibold)',
  fontSize: 'var(--font-size-sm)',
};

export const priceWasStyle: CSSProperties = {
  color: 'var(--color-text-muted)',
  fontWeight: 'var(--font-weight-regular)',
  textDecoration: 'line-through',
  // Prototype `.price.was`: 12.
  fontSize: 'var(--font-size-3xs)',
  marginRight: 'var(--space-1)',
};

export const flyerBadgeStyle: CSSProperties = {
  // Prototype `.badge`: tiny; the dedicated 2xs tier now exists.
  fontSize: 'var(--font-size-2xs)',
  fontWeight: 'var(--font-weight-semibold)',
  color: 'var(--color-status-danger)',
  backgroundColor: 'var(--color-status-danger-subtle, var(--color-bg-muted))',
  padding: '0 var(--space-1)',
  borderRadius: 'var(--radius-sm)',
  marginLeft: 'var(--space-1)',
};

// T27.2.7.9.5 — the per-store deal cell in the Where to buy picker rows: the
// sale price + FLYER on one line, the validity ("ends Jun 4") quietly beneath,
// right-aligned so the price column stays tidy across store rows.
export const storeDealCellStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  flexShrink: 0,
  gap: 'var(--space-1)',
};

export const storeDealPriceRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
};

export const dealValidityStyle: CSSProperties = {
  // Prototype's inline sub-line scale: 11.
  fontSize: 'var(--font-size-2xs)',
  color: 'var(--color-text-muted)',
};

// T27.2.7.9.5 (active-vs-upcoming) — a NOT-YET-STARTED deal renders in a
// distinct, lower-emphasis style so it never reads as a current price: the price
// drops from the bold danger-toned on-sale price to the secondary text color, the
// FLYER badge swaps the danger red for a quiet neutral chip, and the validity
// reads "starts <date>" in the muted color. Token-only (CP5), no raw values.
export const upcomingPriceStyle: CSSProperties = {
  fontWeight: 'var(--font-weight-medium)',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-text-secondary)',
};

export const upcomingFlyerBadgeStyle: CSSProperties = {
  fontSize: 'var(--font-size-2xs)',
  fontWeight: 'var(--font-weight-semibold)',
  color: 'var(--color-text-secondary)',
  backgroundColor: 'var(--color-bg-muted)',
  border: 'var(--border-width-hairline) solid var(--color-border-strong)',
  padding: '0 var(--space-1)',
  borderRadius: 'var(--radius-sm)',
  marginLeft: 'var(--space-1)',
};

export const upcomingValidityStyle: CSSProperties = {
  fontSize: 'var(--font-size-2xs)',
  color: 'var(--color-text-muted)',
  fontStyle: 'italic',
};

// T27.2.7.9.6 — the browse list of a store's matched Flyer deals (shown when a
// store has more than one). Indented under the store row (aligned past the radio
// + gap) so it reads as that store's deals, with a quiet leading rule.
export const dealBrowseListStyle: CSSProperties = {
  // Indent to align with the store name (radio width `1rem` + the row gap
  // `--space-2`), so the sub-list sits under the store, not the radio.
  marginLeft: 'calc(1rem + var(--space-2))',
  borderLeftWidth: '0.0625rem',
  borderLeftStyle: 'solid',
  borderLeftColor: 'var(--color-border-subtle)',
  paddingLeft: 'var(--space-2)',
  marginBottom: 'var(--space-2)',
};

// One deal in the browse list: a pinnable area (flyer title + price) on the left
// and a quiet "View" link on the right. A flex row so the View link trails. The
// hairline divider sits BETWEEN rows only — the last row uses
// `dealBrowseRowLastStyle` (no bottom border), so a single-deal store has no
// stray line and a multi-deal store has no trailing line under its final row.
export const dealBrowseRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-2)',
  borderBottomWidth: '0.0625rem',
  borderBottomStyle: 'solid',
  borderBottomColor: 'var(--color-border-subtle)',
};

// The LAST browse row — same layout, no bottom divider (the divider separates
// rows; there's nothing below the last one to separate from).
export const dealBrowseRowLastStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-2)',
};

// The pinnable part of a browse row: the radio + flyer title + the price cell.
// A button (pins the deal); the trailing "View" is a SIBLING (a nested button is
// invalid). Reuses the store-row layout rhythm.
export const dealPinButtonStyle: CSSProperties = {
  flex: 1,
  minWidth: 0,
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-2)',
  padding: 'var(--space-2) 0',
  minHeight: '2.75rem',
  background: 'transparent',
  border: 'none',
  textAlign: 'left',
  color: 'inherit',
  cursor: 'pointer',
  fontFamily: 'var(--font-family-sans)',
};

// The pin glyph for a deal browse row. A deal row is a pin/unpin TOGGLE, not a
// single-select, so it uses a map-pin icon (not the store-row radio). Mirrors the
// radio's 1rem box; tints via `color` so the inline currentColor SVG inherits it.
export const dealPinIconStyle: CSSProperties = {
  width: '1rem',
  height: '1rem',
  flex: '0 0 auto',
  color: 'var(--color-border-strong)',
};

// Pinned deal — accent tint (vs the quiet grey of an unpinned row).
export const dealPinIconActiveStyle: CSSProperties = {
  ...dealPinIconStyle,
  color: 'var(--color-accent-primary)',
};

// The flyer title in a browse row — the label that tells the deals apart. Takes
// the row's flexible width and truncates so a long title never pushes the price
// cell off the row.
export const dealBrowseTitleStyle: CSSProperties = {
  flex: 1,
  minWidth: 0,
  fontSize: 'var(--font-size-smPlus)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

// The "View" link on a browse row — reopens the flyer in the shared deal overlay.
// A quiet accent-toned text button. The LABEL stays visually compact, but the
// TAP AREA clears the 44px touch floor (frontend-standards §5): the button is a
// centered inline-flex box sized to `2.75rem` min in both axes, so the hit target
// grows around the small label rather than the label itself becoming a large box.
export const dealViewButtonStyle: CSSProperties = {
  flex: '0 0 auto',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '2.75rem',
  minWidth: '2.75rem',
  fontSize: 'var(--font-size-2sm)',
  fontWeight: 'var(--font-weight-semibold)',
  color: 'var(--color-accent-primary)',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  padding: 'var(--space-1) var(--space-2)',
  fontFamily: 'var(--font-family-sans)',
};

export const pickedLineStyle: CSSProperties = {
  // Prototype `.pickedline`: 12.5.
  fontSize: 'var(--font-size-2sm)',
  color: 'var(--color-text-muted)',
  margin: 'var(--space-2) 0 var(--space-1)',
};

export const pickedLineActiveStyle: CSSProperties = {
  ...pickedLineStyle,
  color: 'var(--color-text-muted)',
  fontWeight: 'var(--font-weight-semibold)',
};

export const forthcomingStyle: CSSProperties = {
  // Prototype's no-offer note: 13.
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-muted)',
  padding: 'var(--space-1) 0 var(--space-2)',
};

export const moreBrandsStyle: CSSProperties = {
  // Prototype `.morebrands`: 12.5.
  fontSize: 'var(--font-size-2sm)',
  color: 'var(--color-accent-primary)',
  fontWeight: 'var(--font-weight-semibold)',
  marginTop: 'var(--space-2)',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  padding: 0,
  fontFamily: 'var(--font-family-sans)',
};

// T27.2.7.9.6 — the "Buy regular here" control's row wrapper. Sits above the
// picked-line when a flyer is pinned; a quiet inline action, left-aligned.
export const buyRegularRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  marginTop: 'var(--space-2)',
};

// "Buy regular here" — clears the pinned flyer + carried offer, keeps the store.
// A removal action, so a muted (not accent) text button distinct from the pin /
// View accent links; full touch target.
export const buyRegularButtonStyle: CSSProperties = {
  fontSize: 'var(--font-size-2sm)',
  fontWeight: 'var(--font-weight-semibold)',
  color: 'var(--color-text-muted)',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  padding: 'var(--space-1) 0',
  minHeight: '2.75rem',
  fontFamily: 'var(--font-family-sans)',
};

// `itemNote` — the per-trip note (`notes`). Editable in Planning (a textarea),
// read-only in Shopping (a plain line). Authored here; saves through the same
// orchestrator path as quantity (`handleUpdateItem({ notes })`). Sits between the
// quantity row and the where-to-buy section in the opened-up view.
export const noteRowStyle: CSSProperties = {
  margin: 'var(--space-1) 0 var(--space-3)',
};

export const noteLabelStyle: CSSProperties = {
  display: 'block',
  // Mirror the where-head small uppercase label.
  fontSize: 'var(--font-size-2xs)',
  fontWeight: 'var(--font-weight-semibold)',
  textTransform: 'uppercase',
  letterSpacing: '0.03em',
  color: 'var(--color-text-muted)',
  marginBottom: 'var(--space-1)',
};

export const noteInputStyle: CSSProperties = {
  width: '100%',
  minHeight: '2.75rem',
  // Auto-grows with content (the row sets `height` from `scrollHeight` on input
  // + on mount), so the manual drag-handle is gone — `resize: 'none'`. The cap
  // is ~5 lines of `font-size-sm` at the normal line-height plus the field's own
  // vertical padding (`space-2` ×2); past it the field scrolls. Token-only (CP5).
  resize: 'none',
  maxHeight:
    'calc(var(--font-size-sm) * var(--line-height-normal) * 5 + var(--space-2) * 2)',
  overflowY: 'auto',
  lineHeight: 'var(--line-height-normal)',
  padding: 'var(--space-2)',
  borderWidth: '0.0625rem',
  borderStyle: 'solid',
  borderColor: 'var(--color-border-subtle)',
  borderRadius: 'var(--radius-md)',
  backgroundColor: 'var(--color-bg-raised)',
  color: 'var(--color-text-primary)',
  fontFamily: 'var(--font-family-sans)',
  fontSize: 'var(--font-size-sm)',
  boxSizing: 'border-box',
};

// The footer row (`itemFoot`): the who-added byline on the left, the fine-tune
// link on the right (planning only). Sits above a dashed top divider.
export const itemFootStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-2)',
  marginTop: 'var(--space-3)',
  paddingTop: 'var(--space-2)',
  borderTopWidth: '0.0625rem',
  borderTopStyle: 'dashed',
  borderTopColor: 'var(--color-border-subtle)',
};

// `addedByByline` — who added the item + when, quiet.
export const addedByBylineStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 'var(--space-1)',
  flex: 1,
  minWidth: 0,
  fontSize: 'var(--font-size-2sm)',
  color: 'var(--color-text-muted)',
};

// `fineTuneLink` — opens HouseholdProduct settings (planning only). A quiet
// trailing link in the footer; opts out of the global 44px min-height (the row +
// the expanded panel are the large affordances) so the footer stays one line.
export const fineTuneLinkStyle: CSSProperties = {
  minHeight: 0,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 'var(--space-1)',
  flex: '0 0 auto',
  fontSize: 'var(--font-size-2sm)',
  fontWeight: 'var(--font-weight-semibold)',
  color: 'var(--color-text-secondary)',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  fontFamily: 'var(--font-family-sans)',
  padding: 0,
};

export const sectionHeadStyle: CSSProperties = {
  // Prototype `.sech`: 12 (xs rounded this up to 13).
  fontSize: 'var(--font-size-3xs)',
  fontWeight: 'var(--font-weight-semibold)',
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: 'var(--color-text-muted)',
  // Prototype `.sech` margin: 18 / 2 / 8. There is no space-5 tier; use the
  // existing space-4 (16) top / space-1 sides / space-2 bottom — close enough
  // (the 2-unit imperceptible delta the design tolerates).
  margin: 'var(--space-4) var(--space-1) var(--space-2)',
};

// T12.5.1 (E) — the stacked preference badge set on a matched offer row. Same
// tone palette + wrap behaviour as the Deals surface; the designer pass owns the
// final colours / truncation. The relative order is fixed by `BADGE_ORDER`.
export const badgeRowStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 'var(--space-1)',
  marginTop: 'var(--space-1)',
};

const badgeToneBase: CSSProperties = {
  display: 'inline-block',
  padding: '0 var(--space-1)',
  borderRadius: 'var(--radius-sm)',
  fontSize: 'var(--font-size-3xs)',
  fontWeight: 'var(--font-weight-bold)',
};
const badgePositiveStyle: CSSProperties = {
  ...badgeToneBase,
  backgroundColor: 'var(--color-accent-subtle)',
  color: 'var(--color-accent-primary)',
};
const badgeSubstituteStyle: CSSProperties = {
  ...badgeToneBase,
  backgroundColor: 'var(--color-bg-raised)',
  color: 'var(--color-text-muted)',
};
const badgeNegativeStyle: CSSProperties = {
  ...badgeToneBase,
  backgroundColor: 'var(--color-status-danger-subtle, var(--color-bg-raised))',
  color: 'var(--color-status-danger, var(--color-text-muted))',
};

export function badgeStyleFor(
  tone: 'positive' | 'substitute' | 'negative',
): CSSProperties {
  if (tone === 'positive') return badgePositiveStyle;
  if (tone === 'substitute') return badgeSubstituteStyle;
  return badgeNegativeStyle;
}
